import * as pc from 'playcanvas'
import {
  ensureAssetLoaded,
  getCachedAsset,
  getCharacterEntry,
  onAssetReady,
} from './assets'
import type { VoxelAvatarState } from './avatarState'
import {
  KITCHEN_CENTER,
  standPlacement,
  type CharacterPlacement,
} from './world/layout'
import { worldRoot } from './world/worldRoot'

export const HUMAN_VARIANTS = [
  'char_business_man.glb',
  'char_casual_a.glb',
  'char_casual_b.glb',
  'char_worker.glb',
] as const

export type HumanVariant = (typeof HUMAN_VARIANTS)[number]
export const SYNTY_PROOF_VARIANT = 'char_synty_hipster_guy.glb' as const
export const SYNTY_ROSTER_VARIANTS = [
  'char_synty_hipster_guy_v2.glb',
  'char_synty_gamer_girl.glb',
  'char_synty_biker.glb',
  'char_synty_firefighter.glb',
  'char_synty_grandpa.glb',
  'char_synty_hipster_girl.glb',
  'char_synty_jock.glb',
  'char_synty_paramedic.glb',
  'char_synty_punk_girl.glb',
  'char_synty_punk_guy.glb',
  'char_synty_grandma.glb',
  'char_synty_summer_girl.glb',
] as const
export type SyntyRosterVariant = (typeof SYNTY_ROSTER_VARIANTS)[number]
export type CharacterVariant =
  | HumanVariant
  | 'char_robot.glb'
  | typeof SYNTY_PROOF_VARIANT
  | SyntyRosterVariant

/** World position + yaw of the desk station a character belongs to. */
export interface StationPlacement {
  position: [number, number, number]
  rotY: number
}

export interface CharacterHandle {
  entity: pc.Entity
  setAnimState(state: VoxelAvatarState): void
  /**
   * Suspend or resume this character's scene submission and animation work
   * without destroying its entity or releasing shared source assets.
   */
  setActive?(active: boolean): void
  /**
   * Bind the character to a desk station. The character then manages its own
   * transform: grounded at the standing work spot, then walking toward the
   * kitchen after a ground-floor completion.
   */
  setStation(station: StationPlacement): void
  /** Runtime visual selection only; projected identity remains Portal-owned. */
  getVisualStatus?(): CharacterVisualStatus
  destroy(): void
}

export interface CharacterVisualStatus {
  requested: CharacterVariant
  active: CharacterVariant
  fallback: boolean
  animated?: boolean
  animationPlaying?: boolean
  animationClipCount?: number
  animationSource?: string
  animationAssetId?: number
  animationTargetsResolved?: boolean
  sceneActive?: boolean
  error?: string
}

/**
 * Primary clip per avatar state — human characters (CharacterArmature prefix).
 * Clip names match manifest.json exactly.
 */
export const HUMAN_CLIP_MAP: Readonly<Record<VoxelAvatarState, string>> = {
  idle:        'CharacterArmature|Idle',
  working:     'CharacterArmature|Interact',
  waiting:     'CharacterArmature|Idle_Neutral',
  celebrating: 'CharacterArmature|Wave',
  slumped:     'CharacterArmature|Death',
  alarmed:     'CharacterArmature|Run',
}

/** Walk clip used for the completion stroll to the kitchen. */
export const HUMAN_WALK_CLIP = 'CharacterArmature|Walk'

/** Named clips produced by the pinned TASK-1184 Synty conversion job. */
export const SYNTY_CLIP_MAP: Readonly<Record<VoxelAvatarState, string>> = {
  idle:        'Synty|Idle_Base',
  working:     'Synty|Idle_Thoughtful',
  waiting:     'Synty|Idle_ArmsFolded',
  celebrating: 'Synty|Idle_ArmsFolded',
  slumped:     'Synty|Idle_Base',
  alarmed:     'Synty|Walk',
}

export const SYNTY_WALK_CLIP = 'Synty|Walk'
export const SYNTY_IDLE_CLIPS = [
  'Synty|Idle_Base',
  'Synty|Idle_Standing',
  'Synty|Idle_Thoughtful',
  'Synty|Idle_ArmsFolded',
] as const
export const SYNTY_REQUIRED_CLIPS = [
  ...new Set([...Object.values(SYNTY_CLIP_MAP), ...SYNTY_IDLE_CLIPS, SYNTY_WALK_CLIP]),
] as const
/** Keeps the deepest licensed idle foot phase at or above the floor. */
export const SYNTY_GROUND_CLEARANCE_M = 0.05
/** Every placeable L1/L2 roster slot uses the licensed, shared-bank path. */
export const SYNTY_ROSTER_SLOTS = 32
/** @deprecated Compatibility alias for the original single-character proof. */
export const SYNTY_PROOF_ROSTER_SLOTS = SYNTY_ROSTER_SLOTS

/** Arena-owned, stable visual selection independent of Portal identity fields. */
export function pickSyntyRosterVariant(seed: string): SyntyRosterVariant {
  return SYNTY_ROSTER_VARIANTS[djb2(`${seed}|synty-roster`) % SYNTY_ROSTER_VARIANTS.length]
}

/** Resolve the catalog-declared external bank for one mesh-only roster visual. */
export function syntyAnimationBankFor(
  variant: SyntyRosterVariant,
): string | undefined {
  return getCharacterEntry(variant)?.runtime?.animationBank
}

/**
 * Primary clip per avatar state — robot character (RobotArmature prefix).
 * Clip names match manifest.json exactly.
 */
export const ROBOT_CLIP_MAP: Readonly<Record<VoxelAvatarState, string>> = {
  idle:        'RobotArmature|Robot_Idle',
  working:     'RobotArmature|Robot_Sitting',
  waiting:     'RobotArmature|Robot_Idle',
  celebrating: 'RobotArmature|Robot_Wave',
  slumped:     'RobotArmature|Robot_Death',
  alarmed:     'RobotArmature|Robot_Running',
}

/** Deterministically pick one of the 4 human variants from a run ID. */
export function pickVariant(runId: string): HumanVariant {
  let h = 0
  for (let i = 0; i < runId.length; i++) {
    h = (h * 31 + runId.charCodeAt(i)) >>> 0
  }
  return HUMAN_VARIANTS[h % HUMAN_VARIANTS.length]
}

/** Engine-safe state id: pipes/dots in GLB clip names break state lookup. */
export function stateName(clipName: string): string {
  return clipName.replace(/[^A-Za-z0-9_]/g, '_')
}

// ── Ambient-life tuning ───────────────────────────────────────────────────

/** Walking speed for the completion stroll (m/s). */
export const WALK_SPEED_MPS = 1.4
/** How long the completed character walks toward the kitchen (s). */
export const WALK_DURATION_S = 4
/** Full period of the waiting look-around yaw sweep (s). */
export const LOOK_SWEEP_PERIOD_S = 7
/** Peak yaw deviation of the look-around sweep (deg). */
export const LOOK_SWEEP_AMPLITUDE_DEG = 40
/** Max per-character idle clip phase offset (s) — desyncs identical idles. */
export const IDLE_DESYNC_MAX_S = 3
/** Keep the authored feet just clear of the finished floor across every rig. */
export const CHARACTER_GROUND_CLEARANCE_M = 0.015
/** The robot asset extends slightly farther below its authored origin. */
export const ROBOT_GROUND_CLEARANCE_M = 0.025
/** Duration of one deterministic idle-variation cycle. */
export const IDLE_VARIATION_PERIOD_S = 9
/** A short gesture window inside some idle cycles. */
export const IDLE_GESTURE_DURATION_S = 1.25
/** Small desk-focused yaw motion that makes even subtle idle clips readable. */
export const IDLE_DESK_SWAY_AMPLITUDE_DEG = 4
/** Short cross-fade used for idle↔walk and all other live state changes. */
export const CHARACTER_TRANSITION_SECONDS = 0.15

/** Safe, prop-free human idles shared by every shipped office character. */
export const HUMAN_IDLE_CLIPS = [
  'CharacterArmature|Idle',
  'CharacterArmature|Idle_Neutral',
] as const

export interface DeskModelPosture {
  y: number
  pitch: number
}

/**
 * The shipped human armature has no sitting clip. Lowering the whole standing
 * rig to chair height pushed both feet through the slab, so human desk actors
 * now stay grounded at a standing-height workstation. Desk motion comes from
 * the animation clip rather than rotating the whole rig through the floor.
 * The robot keeps its native Robot_Sitting clip.
 */
export function deskModelPosture(
  _state: VoxelAvatarState,
  _isWalking = false,
): DeskModelPosture {
  return {
    y: CHARACTER_GROUND_CLEARANCE_M,
    pitch: 0,
  }
}

function djb2(s: string): number {
  let h = 5381
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h + s.charCodeAt(i)) >>> 0
  }
  return h
}

/**
 * A deterministic, desynchronised idle selector. Most cycles alternate the
 * two natural standing idles; roughly one cycle in three gets a brief wave.
 * It uses only clips shared by every HUMAN_VARIANTS rig.
 */
export function humanIdleClipFor(seed: string, elapsedSeconds: number): string {
  const safeElapsed = Number.isFinite(elapsedSeconds) ? Math.max(0, elapsedSeconds) : 0
  const shifted = safeElapsed + phaseOffsetSeconds(seed, IDLE_VARIATION_PERIOD_S)
  const cycle = Math.floor(shifted / IDLE_VARIATION_PERIOD_S)
  const within = shifted - cycle * IDLE_VARIATION_PERIOD_S
  const gestureCycle = djb2(`${seed}|idle-gesture|${cycle}`) % 3 === 0
  if (gestureCycle && within >= IDLE_VARIATION_PERIOD_S - IDLE_GESTURE_DURATION_S) {
    return HUMAN_CLIP_MAP.celebrating
  }
  return HUMAN_IDLE_CLIPS[(djb2(`${seed}|idle-base`) + cycle) % HUMAN_IDLE_CLIPS.length]
}

/** Deterministic role-appropriate Synty desk pose, offset across clients. */
export function syntyIdleClipFor(seed: string, elapsedSeconds: number): string {
  const safeElapsed = Number.isFinite(elapsedSeconds) ? Math.max(0, elapsedSeconds) : 0
  const shifted = safeElapsed + phaseOffsetSeconds(seed, IDLE_VARIATION_PERIOD_S)
  const cycle = Math.floor(shifted / IDLE_VARIATION_PERIOD_S)
  return SYNTY_IDLE_CLIPS[(djb2(`${seed}|synty-desk`) + cycle) % SYNTY_IDLE_CLIPS.length]
}

/** Pure compatibility gate used before replacing a known-good fallback. */
export function hasRequiredCharacterClips(
  clips: readonly string[],
  required: readonly string[],
): boolean {
  const available = new Set(clips)
  return required.every((clip) => available.has(clip))
}

/** Validate the actual container tracks before a licensed rig replaces fallback. */
export function assetHasRequiredClips(asset: pc.Asset): boolean {
  const resource = asset.resource as unknown as { animations?: pc.Asset[] } | undefined
  const names = (resource?.animations ?? []).map((animation) => {
    const track = animation.resource as pc.AnimTrack | undefined
    return track?.name ?? animation.name
  })
  return hasRequiredCharacterClips(names, SYNTY_REQUIRED_CLIPS)
}

/**
 * Validate animation targets against the exact entity graph PlayCanvas will
 * bind. Clip-name compatibility alone is insufficient: the evaluator silently
 * skips curves whose target paths do not resolve.
 */
export function animationAssetTargetsResolve(
  entity: pc.Entity,
  asset: pc.Asset,
): boolean {
  const resource = asset.resource as unknown as { animations?: pc.Asset[] } | undefined
  const tracks = (resource?.animations ?? [])
    .map((animation) => animation.resource as pc.AnimTrack | undefined)
    .filter((track): track is pc.AnimTrack => track !== undefined)
  if (tracks.length === 0) return false

  const binder = new pc.DefaultAnimBinder(entity)
  let targetCount = 0
  for (const track of tracks) {
    const curves = track.curves as unknown as Array<{ paths?: unknown[] }>
    for (const curve of curves) {
      for (const path of curve.paths ?? []) {
        targetCount += 1
        try {
          if (!binder.resolve(path as never)) return false
        } catch {
          return false
        }
      }
    }
  }
  return targetCount > 0
}

/** Keep idle workers focused on the monitor while avoiding frozen clone poses. */
export function idleDeskYawOffset(elapsedSeconds: number, phaseSeconds: number): number {
  const elapsed = Number.isFinite(elapsedSeconds) ? elapsedSeconds : 0
  const phase = Number.isFinite(phaseSeconds) ? phaseSeconds : 0
  return IDLE_DESK_SWAY_AMPLITUDE_DEG * Math.sin(
    (2 * Math.PI * (elapsed + phase)) / IDLE_VARIATION_PERIOD_S,
  )
}

/**
 * Deterministic per-character phase offset in [0, period), seeded by run id.
 * Same seed → same offset, so idles desync consistently across clients.
 */
export function phaseOffsetSeconds(seed: string, period: number): number {
  if (period <= 0) return 0
  return ((djb2(seed) % 1000) / 1000) * period
}

/** Slow sinusoidal yaw sweep (deg) for waiting characters looking around. */
export function lookYawOffset(tSeconds: number, phaseSeconds: number): number {
  return (
    LOOK_SWEEP_AMPLITUDE_DEG *
    Math.sin((2 * Math.PI * (tSeconds + phaseSeconds)) / LOOK_SWEEP_PERIOD_S)
  )
}

/**
 * Deterministic point in the kitchen zone a completed character walks toward.
 * Spread ±1.5 m around KITCHEN_CENTER so walkers do not pile onto one spot.
 */
export function kitchenTargetFor(seed: string): [number, number, number] {
  const dx = ((djb2(`kx|${seed}`) % 1000) / 999) * 3 - 1.5
  const dz = ((djb2(`kz|${seed}`) % 1000) / 999) * 3 - 1.5
  return [KITCHEN_CENTER[0] + dx, 0, KITCHEN_CENTER[1] + dz]
}

/**
 * Position along the straight walk path from `start` toward `target` after
 * `tSeconds` at `speed` m/s. Pure and deterministic; clamps at the target.
 */
export function walkPosition(
  start: readonly [number, number, number],
  target: readonly [number, number, number],
  speed: number,
  tSeconds: number,
): [number, number, number] {
  const dx = target[0] - start[0]
  const dz = target[2] - start[2]
  const dist = Math.hypot(dx, dz)
  if (dist === 0) return [start[0], start[1], start[2]]
  const travelled = Math.min(Math.max(speed * tSeconds, 0), dist)
  const f = travelled / dist
  return [start[0] + dx * f, start[1], start[2] + dz * f]
}

/**
 * World yaw (deg) facing from `start` toward `target`. Yaw 0 faces +Z —
 * the same convention as the station props (the chair uses yaw 180 to face
 * local -Z).
 */
export function walkYawDeg(
  start: readonly [number, number, number],
  target: readonly [number, number, number],
): number {
  return (Math.atan2(target[0] - start[0], target[2] - start[2]) * 180) / Math.PI
}

// ── Engine-correct anim setup ─────────────────────────────────────────────

/** Structural slice of pc.AnimComponent used by the setup sequence (testable). */
export interface CharacterAnimComponent {
  loadStateGraph(graph: pc.AnimStateGraph): void
  baseLayer: {
    assignAnimation(nodePath: string, track: pc.AnimTrack): void
    play(name: string): void
    activeStateCurrentTime: number
    readonly activeState: string
    readonly playing: boolean
  } | null
  enabled: boolean
  playing: boolean
}

/**
 * Wire a code-built state graph onto an anim component and START it — the
 * engine-correct sequence for `activate: false` components:
 *
 *  1. `loadStateGraph` — an anim component has NO layers until a graph is
 *     loaded; states get SANITIZED ids (a name the engine can't find falls
 *     through to an entity-node-path lookup and crashes).
 *  2. `baseLayer.assignAnimation` for EVERY state — the component must not
 *     evaluate until every state has a track (a live controller with an
 *     un-tracked state crashes every frame).
 *  3. `enabled = true` + `playing = true` — the component-level gates the
 *     AnimComponentSystem checks each frame.
 *  4. `baseLayer.play(initialState)` — THE CRITICAL STEP. `component.playing`
 *     only sets the component flag; each layer's AnimController has its OWN
 *     `_playing` flag which, with `activate: false`, nothing else ever sets.
 *     `AnimController.update()` early-returns while its `_playing` is false,
 *     so without this call no clip is ever instantiated, the START→state
 *     transition never fires and the character is frozen in bind pose
 *     (the original "characters not moving" bug). `layer.play(state)` both
 *     transitions to the state and starts the controller.
 *  5. Optional phase offset via `activeStateCurrentTime` so identical idle
 *     clips don't run in lockstep.
 *
 * Returns the number of clips assigned (0 = nothing to play).
 */
export function setupCharacterAnim(
  anim: CharacterAnimComponent,
  clips: ReadonlyArray<{ name: string; track: pc.AnimTrack }>,
  initialClipName: string,
  phaseOffset = 0,
): number {
  if (clips.length === 0) return 0
  // If everything that survived loading is a hold clip (Death), building the
  // graph would spawn the character mid-collapse. A static character beats a
  // corpse: skip anim setup entirely.
  if (clips.every((c) => /death/i.test(c.name))) return 0

  // Death/collapse AND sitting clips play once and hold the final pose;
  // everything else loops. "Sitting" is a stand→sit transition, so looping it
  // makes a seated character repeatedly pop up and sit back down — it must hold
  // its final seated frame instead. GLB clip lists are alphabetical, which puts
  // Death FIRST — so the fallback initial state must be an Idle-ish clip, never
  // clips[0].
  const isHoldClip = (name: string) => /death|sitting/i.test(name)
  const initial =
    (clips.some((c) => c.name === initialClipName) ? initialClipName : null) ??
    clips.find((c) => /idle/i.test(c.name) && !isHoldClip(c.name))?.name ??
    clips.find((c) => !isHoldClip(c.name))?.name ??
    clips[0].name

  const graph = new pc.AnimStateGraph({
    layers: [
      {
        name: 'BaseLayer',
        states: [
          { name: 'START' },
          ...clips.map((c) => ({ name: stateName(c.name), speed: 1, loop: !isHoldClip(c.name) })),
          { name: 'END' },
        ],
        transitions: [
          { from: 'START', to: stateName(initial), time: 0, priority: 0 },
        ],
      },
    ],
    parameters: {},
  })
  anim.loadStateGraph(graph)

  const layer = anim.baseLayer
  if (!layer) return 0
  for (const clip of clips) {
    layer.assignAnimation(stateName(clip.name), clip.track)
  }

  anim.enabled = true
  anim.playing = true

  layer.play(stateName(initial))
  if (phaseOffset > 0) {
    layer.activeStateCurrentTime = phaseOffset
  }
  return clips.length
}

/** States that were successfully assigned a real track, per character root. */
const _assignedStates = new WeakMap<pc.Entity, Set<string>>()

/** Register the assigned state names for an entity (called by spawn wiring). */
export function registerAssignedStates(entity: pc.Entity, clipNames: Iterable<string>): void {
  _assignedStates.set(entity, new Set([...clipNames].map(stateName)))
}

export function playClip(entity: pc.Entity, clipName: string): void {
  const layer = entity.anim?.baseLayer
  if (!layer) return
  const state = stateName(clipName)
  // Transitioning to a state without a bound track doesn't throw here — it
  // detonates inside the engine's next frame update. Refuse unknown states.
  const known = _assignedStates.get(entity)
  if (known && !known.has(state)) return
  // Same state already active normally needs no transition. If the component
  // or layer was paused by asset hydration/context recovery, restart that
  // exact loop so roster agents cannot remain frozen in a valid idle state.
  if (layer.activeState === state) {
    // Death and seated poses are deliberately held on a still frame. Replaying
    // them here would turn an intentional hold into apparent idle motion.
    if (/death|sitting/i.test(clipName)) return
    if (!layer.playing || entity.anim?.playing !== true || entity.anim.enabled !== true) {
      if (entity.anim) {
        entity.anim.enabled = true
        entity.anim.playing = true
        entity.anim.speed = 1
      }
      try { layer.play(state) } catch { /* keep the current pose on engine failure */ }
    }
    return
  }
  // Restore normal speed in case a previous state froze the layer (Death hold).
  if (entity.anim) entity.anim.speed = 1
  try {
    layer.transition(state, CHARACTER_TRANSITION_SECONDS)
  } catch {
    // Missing state — leave current animation rather than crashing the frame.
  }
}

/**
 * Spawn a GLB character into the scene at `position`.
 *
 * If the asset is already preloaded (via `preloadAssets`), the render
 * hierarchy and anim component are attached immediately; otherwise an empty
 * entity is returned and will be populated via `onAssetReady` when the
 * asset finishes loading — preserving whatever anim state was set in the
 * interim.
 *
 * `seed` (usually the run id) drives deterministic idle variation and phase
 * offsets. Human characters bound to a desk remain grounded at its standing
 * work spot because these rigs have no sitting clip; completion can still
 * walk toward the kitchen. Stationless characters keep their externally-set
 * transform. The server robot keeps its authored sitting animation.
 *
 * `char_robot.glb` is reserved for the server room (`Robot_Sitting`);
 * desk stations should pass a `HumanVariant` obtained via `pickVariant`.
 */
export function spawnCharacter(
  app: pc.Application,
  variant: CharacterVariant,
  position: [number, number, number],
  seed?: string,
  options: {
    castShadows?: boolean
    visualAsset?: pc.Asset
    animationAsset?: pc.Asset
    animationSource?: string
    ownerTags?: readonly string[]
    /** Explicit spatial owner for non-HQ character systems. */
    parent?: pc.Entity
    /** Preserve live skinned bounds for small cohorts that require foot-contact QA. */
    dynamicBounds?: boolean
  } = {},
): CharacterHandle {
  const isRobot = variant === 'char_robot.glb'
  const isLegacySyntyProof = variant === SYNTY_PROOF_VARIANT
  const isSynty = isLegacySyntyProof ||
    SYNTY_ROSTER_VARIANTS.includes(variant as SyntyRosterVariant)
  const clipMap = isRobot ? ROBOT_CLIP_MAP : isSynty ? SYNTY_CLIP_MAP : HUMAN_CLIP_MAP
  const walkClip = isSynty ? SYNTY_WALK_CLIP : HUMAN_WALK_CLIP
  const waveClip = isRobot
    ? 'RobotArmature|Robot_Wave'
    : isSynty
      ? SYNTY_CLIP_MAP.celebrating
      : 'CharacterArmature|Wave'
  const phaseSeed = seed ?? variant

  const root = new pc.Entity(`char-${variant}`)
  for (const tag of options.ownerTags ?? []) root.tags.add(tag)
  root.setLocalPosition(position[0], position[1], position[2])

  let currentState: VoxelAvatarState = 'idle'
  let sceneActive = true
  let station: StationPlacement | null = null
  let baseYaw = 0
  let waitElapsed = 0
  let lookElapsed = 0
  let idleElapsed = 0
  let waving = false
  let destroyed = false
  let modelRoot: pc.Entity | null = null
  let selfCheckTimer: ReturnType<typeof setTimeout> | null = null
  // Completion stroll toward the kitchen (humans with a station only).
  let walk: {
    start: [number, number, number]
    target: [number, number, number]
    elapsed: number
    arrived: boolean
  } | null = null
  const lookPhase = phaseOffsetSeconds(phaseSeed, LOOK_SWEEP_PERIOD_S)
  const idlePhase = phaseOffsetSeconds(phaseSeed, IDLE_DESYNC_MAX_S)
  const idleMotionPhase = phaseOffsetSeconds(phaseSeed, IDLE_VARIATION_PERIOD_S)
  // Tracks which animation clips are actually assigned to the anim component.
  const assignedClips = new Set<string>()
  let animationTargetsResolved: boolean | undefined
  // Stores AnimTrack references so Death can be re-assigned with loop:false.
  const animTracks = new Map<string, pc.AnimTrack>()

  function applyModelPosture(): void {
    if (!modelRoot) return
    if (isRobot) {
      modelRoot.setLocalPosition(0, ROBOT_GROUND_CLEARANCE_M, 0)
      return
    }
    if (isSynty) {
      modelRoot.setLocalPosition(0, SYNTY_GROUND_CLEARANCE_M, 0)
      return
    }
    const posture = station
      ? deskModelPosture(currentState, walk !== null)
      : { y: CHARACTER_GROUND_CLEARANCE_M, pitch: 0 }
    modelRoot.setLocalPosition(0, posture.y, 0)
    modelRoot.setLocalEulerAngles(posture.pitch, 0, 0)
  }

  /** Re-place the character at its station for the current state. */
  function applyPlacement(): void {
    if (!station || isRobot || walk) return
    const p: CharacterPlacement = standPlacement(station.position, station.rotY)
    root.setLocalPosition(p.x, p.y, p.z)
    baseYaw = p.yaw
    root.setEulerAngles(0, baseYaw, 0)
    applyModelPosture()
  }

  function beginWalk(): void {
    if (!station || isRobot) return
    // The completion stroll targets the ground-floor kitchen; an upper-floor
    // agent would walk down through the slabs, so it just stays at its desk.
    if (station.position[1] > 0.1) return
    const from = standPlacement(station.position, station.rotY)
    const start: [number, number, number] = [from.x, 0, from.z]
    const target = kitchenTargetFor(phaseSeed)
    walk = { start, target, elapsed: 0, arrived: false }
    applyModelPosture()
    root.setLocalPosition(start[0], start[1], start[2])
    baseYaw = walkYawDeg(start, target)
    root.setEulerAngles(0, baseYaw, 0)
  }

  function applyState(state: VoxelAvatarState): void {
    // No anim component yet (asset still loading) — attachAndPlay will call
    // applyState again once the asset is ready.
    if (assignedClips.size === 0) return

    // Mid-stroll: keep the Walk clip regardless of the celebrate mapping.
    if (walk && !walk.arrived && state === 'celebrating') {
      if (assignedClips.has(walkClip)) {
        playClip(root, walkClip)
        return
      }
    }

    if (state === 'slumped') {
      const deathClip = clipMap.slumped
      if (!assignedClips.has(deathClip)) {
        // Death clip absent — apply a static bow pose as fallback.
        root.setLocalEulerAngles(45, root.getLocalEulerAngles().y, 0)
        return
      }
      // Death state is non-looping (loop:false in the graph), so it plays
      // once and holds the final collapsed pose on its own.
      playClip(root, deathClip)
      return
    }

    // Variant lacks the mapped clip (e.g. soldier has no Idle_Neutral) —
    // fall back to Idle instead of leaving the previous clip running.
    const requested = state === 'idle' && !isRobot
      ? isSynty
        ? syntyIdleClipFor(phaseSeed, idleElapsed)
        : humanIdleClipFor(phaseSeed, idleElapsed)
      : clipMap[state]
    const clip = assignedClips.has(requested) ? requested : clipMap.idle
    playClip(root, clip)
  }

  function attachAndPlay(asset: pc.Asset): void {
    if (destroyed) return
    // A cached asset can be unloaded out from under us (app teardown unloads
    // every asset in the registry) — deref would throw out of spawnCharacter
    // and abort the caller's whole spawn pass.
    if (!asset.resource) return
    // Guard: attachAndPlay can fire twice (cached path + asset-ready event);
    // a second pass would attach a DUPLICATE render hierarchy and re-running
    // loadStateGraph on a component that already has assignments crashes deep
    // in the engine (observed: "e.getChild is not a function").
    if (root.anim) return
    const container = asset.resource as pc.ContainerResource
    // Desk/ambient characters are numerous and each skinned sub-mesh would be
    // redrawn in every sun cascade. Hero callers may opt in explicitly.
    const renderEntity = container.instantiateRenderEntity({
      castShadows: options.castShadows ?? false,
    })
    modelRoot = renderEntity
    if (isSynty) {
      root.tags.add(`synty-agent:${phaseSeed}`)
      modelRoot.tags.add('synty-character-model-root')
    }
    root.addChild(renderEntity)

    // Skinned meshes recompute precise bounds every frame even when fully
    // off-screen — a fixed character-sized AABB skips that (perf guideline).
    //
    // customAabb is NODE-LOCAL and gets transformed by the node's world
    // matrix. These glTF mesh nodes carry the source rig's scale-100 metre
    // conversion, so a naive metre-sized box became a 120x200x120m world AABB:
    // every character was permanently unculled AND was stretching the shadow
    // cascade fit for the whole office. Divide by the node's world scale.
    for (const mi of renderEntity.findComponents('render') as pc.RenderComponent[]) {
      if (!options.dynamicBounds) {
        const s = mi.entity.getWorldTransform().getScale()
        const sx = Math.abs(s.x) || 1
        const sy = Math.abs(s.y) || 1
        const sz = Math.abs(s.z) || 1
        const dimensions = getCharacterEntry(variant)?.runtime?.dimensions
        const width = dimensions?.[0] ?? 1.2
        const height = dimensions?.[1] ?? 2
        const depth = dimensions?.[2] ?? 1.2
        mi.customAabb = new pc.BoundingBox(
          new pc.Vec3(0, (height / 2) / sy, 0),
          new pc.Vec3(
            Math.max(width / 2, 0.6) / sx,
            (height / 2 + 0.1) / sy,
            Math.max(depth / 2, 0.6) / sz,
          ),
        )
      }
      // PlayCanvas' skinned culling pass does not retain the baked FBX root
      // correction when it tests this legacy rig, even though the measured
      // custom AABB is in the camera frustum. The proof cohort is capped at
      // three one-mesh instances, so always submitting those three is bounded
      // and avoids valid animated characters disappearing from the office.
      if (isLegacySyntyProof) {
        for (const meshInstance of mi.meshInstances) meshInstance.cull = false
      }
    }
    // activate:false — the component must not evaluate until every state has
    // a track; a live controller with an un-tracked state crashes every frame
    // (engine reads activeState.animations[0].loop).
    const anim = root.addComponent('anim', { activate: false }) as unknown as pc.AnimComponent
    const resource = (options.animationAsset?.resource ?? asset.resource) as
      unknown as { animations?: pc.Asset[] }
    const clipAssets = resource.animations ?? []

    try {
      // The clip name lives on the TRACK, not on the sub-asset. PlayCanvas
      // names container animation sub-assets `<container>/animation/<index>`
      // (glb-container-resource.js createAsset), so reading `a.name` yields
      // e.g. "char_worker.glb/animation/0" — which silently defeated every
      // name-based guard here (hold-clip detection, the Idle-preferring
      // initial state, and playClip's membership check), leaving the graph to
      // fall through to clips[0] = CharacterArmature|Death, looping, forever.
      const clips = clipAssets
        .map((a) => {
          const track = a.resource as pc.AnimTrack | undefined
          return { name: track?.name ?? a.name, track: track as pc.AnimTrack }
        })
        .filter((c) => c.track != null)
      if (options.animationAsset) {
        animationTargetsResolved = animationAssetTargetsResolve(
          root,
          options.animationAsset,
        )
        if (!animationTargetsResolved) {
          throw new Error('external animation targets do not resolve on the visual rig')
        }
      }
      if (clips.length < clipAssets.length) {
        console.warn(`[characters] ${variant}: ${clipAssets.length - clips.length} clip track(s) not ready — skipped`)
      }
      const assigned = setupCharacterAnim(
        anim as unknown as CharacterAnimComponent,
        clips,
        currentState === 'idle' && !isRobot
          ? isSynty
            ? syntyIdleClipFor(phaseSeed, idleElapsed)
            : humanIdleClipFor(phaseSeed, idleElapsed)
          : clipMap[currentState],
        idlePhase,
      )
      if (assigned > 0) {
        for (const clip of clips) {
          assignedClips.add(clip.name)
          animTracks.set(clip.name, clip.track)
        }
        registerAssignedStates(root, assignedClips)
      }

      // Apply the current state now that clips are available.
      applyModelPosture()
      applyState(currentState)
      if (!sceneActive) {
        root.enabled = false
        anim.enabled = false
        anim.playing = false
      }

      if (import.meta.env.DEV) {
        // Self-check: 2 s after spawn, log the live playback wiring so any
        // future "characters frozen" regression is immediately visible.
        selfCheckTimer = setTimeout(() => {
          selfCheckTimer = null
          if (destroyed) return
          const layer = (root.anim as unknown as CharacterAnimComponent | undefined)?.baseLayer
          const active = layer?.activeState ?? '<no layer>'
          const trackBound = [...assignedClips].some((clip) => stateName(clip) === active)
          const line =
            `[characters] self-check ${variant} (seed=${phaseSeed}): ` +
            `state=${active} trackBound=${trackBound} layerPlaying=${layer?.playing === true} ` +
            `componentPlaying=${root.anim?.playing === true}`
          if (layer?.playing === true && trackBound) {
            console.info(line)
          } else {
            console.warn(`${line} — PLAYBACK BROKEN`)
          }
        }, 2000)
      }
    } catch (err) {
      // A broken rig must degrade to a STATIC model: disable the component so
      // the engine never evaluates a partially-wired graph (which crashes
      // every frame), and never poison the asset registry callback chain.
      try { anim.enabled = false } catch { /* already unusable */ }
      console.warn(`[characters] anim setup failed for ${variant}:`, err)
    }
  }

  const asset = options.visualAsset ?? getCachedAsset(variant)
  if (asset) {
    attachAndPlay(asset)
  } else {
    onAssetReady(variant, attachAndPlay)
  }

  ;(options.parent ?? worldRoot(app)).addChild(root)

  const onUpdate = (dt: number): void => {
    if (!sceneActive) return
    // Completion stroll — lerp along the path, then celebrate on arrival/stop.
    if (walk && !walk.arrived) {
      walk.elapsed += dt
      if (walk.elapsed >= WALK_DURATION_S) {
        walk.arrived = true
        const p = walkPosition(walk.start, walk.target, WALK_SPEED_MPS, WALK_DURATION_S)
        root.setLocalPosition(p[0], p[1], p[2])
        playClip(root, waveClip)
      } else {
        const p = walkPosition(walk.start, walk.target, WALK_SPEED_MPS, walk.elapsed)
        root.setLocalPosition(p[0], p[1], p[2])
      }
    }

    if (currentState !== 'waiting') {
      waitElapsed = 0
      lookElapsed = 0
      waving = false
    }

    if (currentState === 'idle' && station && !isRobot && !walk) {
      idleElapsed += dt
      root.setEulerAngles(
        0,
        baseYaw + idleDeskYawOffset(idleElapsed, idleMotionPhase),
        0,
      )
      applyState('idle')
    } else {
      idleElapsed = 0
    }

    if (currentState !== 'waiting') return

    // Waiting: slow look-around yaw sweep, desynced per character.
    lookElapsed += dt
    if (station && !isRobot) {
      root.setEulerAngles(0, baseYaw + lookYawOffset(lookElapsed, lookPhase), 0)
    }

    waitElapsed += dt
    if (!waving && waitElapsed >= 6) {
      waving = true
      playClip(root, waveClip)
    } else if (waving && waitElapsed >= 7) {
      waving = false
      waitElapsed = 0
      playClip(root, clipMap.waiting)
    }
  }

  app.on('update', onUpdate)

  return {
    entity: root,

    getVisualStatus(): CharacterVisualStatus {
      const layer = (
        root.anim as unknown as CharacterAnimComponent | undefined
      )?.baseLayer
      return {
        requested: variant,
        active: variant,
        fallback: false,
        animated: assignedClips.size > 0,
        animationPlaying: assignedClips.size > 0 &&
          root.anim?.enabled === true &&
          root.anim.playing === true &&
          layer?.playing === true,
        animationClipCount: assignedClips.size,
        ...(typeof options.animationAsset?.id === 'number'
          ? { animationAssetId: options.animationAsset.id }
          : {}),
        ...(animationTargetsResolved !== undefined
          ? { animationTargetsResolved }
          : {}),
        ...(options.animationSource
          ? { animationSource: options.animationSource }
          : {}),
        sceneActive,
      }
    },

    setAnimState(state: VoxelAvatarState): void {
      if (state === currentState) return
      // Death is absorbing: a slumped character never resurrects (it only
      // despawns). Guards against upstream status flaps replaying the fall.
      if (currentState === 'slumped') {
        console.warn(`[characters] suppressed slumped -> ${state} for ${phaseSeed.slice(0, 12)} (status flap upstream)`)
        return
      }
      const wasCelebrating = currentState === 'celebrating'
      currentState = state
      waitElapsed = 0
      lookElapsed = 0
      idleElapsed = 0
      waving = false
      if (state === 'celebrating' && !isRobot && station) {
        beginWalk()
        // Upper-floor completions deliberately do not walk through slabs, but
        // still need their newly selected seated/celebrating placement.
        if (!walk) applyPlacement()
      } else if (wasCelebrating || state !== 'celebrating') {
        walk = null
        applyPlacement()
      }
      applyState(state)
      if (!sceneActive && root.anim) {
        root.anim.enabled = false
        root.anim.playing = false
        if (root.anim.baseLayer) root.anim.baseLayer.playing = false
      }
    },

    setActive(nextActive: boolean): void {
      if (nextActive === sceneActive) return
      sceneActive = nextActive
      root.enabled = nextActive
      if (!root.anim) return
      root.anim.enabled = nextActive
      root.anim.playing = nextActive
      if (root.anim.baseLayer) root.anim.baseLayer.playing = nextActive
      if (nextActive) applyState(currentState)
    },

    setStation(next: StationPlacement): void {
      station = next
      applyPlacement()
    },

    destroy(): void {
      destroyed = true
      if (selfCheckTimer !== null) clearTimeout(selfCheckTimer)
      app.off('update', onUpdate)
      root.destroy()
    },
  }
}

/**
 * Hydrate one of the mesh-only roster visuals with its catalog-declared
 * animation-only bank. The known-good human stays live until both assets and
 * every required semantic clip pass validation. Shared asset loads are
 * de-duplicated by the catalog cache; each character gets only its own entity
 * hierarchy and anim controller.
 */
export function spawnSyntyRosterCharacter(
  app: pc.Application,
  position: [number, number, number],
  seed: string,
  fallbackVariant: HumanVariant = pickVariant(seed),
  options: {
    parent?: pc.Entity
    ownerTags?: readonly string[]
    dynamicBounds?: boolean
  } = {},
): CharacterHandle {
  const requestedVariant = pickSyntyRosterVariant(seed)
  const animationBank = syntyAnimationBankFor(requestedVariant)
  const ownerTags = options.ownerTags ?? [
    'arena-character-owner',
    'arena-roster-character',
    `arena-roster-agent:${seed}`,
  ] as const
  let currentState: VoxelAvatarState = 'idle'
  let sceneActive = true
  let station: StationPlacement | null = null
  let destroyed = false
  let error: string | undefined
  let activeVariant: CharacterVariant = fallbackVariant

  const spawnPrimary = (
    visualAsset: pc.Asset,
    bankAsset: pc.Asset,
  ): CharacterHandle => {
    if (!visualAsset.resource) throw new Error('Synty mesh-only visual is unavailable')
    if (!assetHasRequiredClips(bankAsset)) {
      throw new Error('required Synty animation-bank clips are missing')
    }
    const next = spawnCharacter(
      app,
      requestedVariant,
      position,
      seed,
      {
        visualAsset,
        animationAsset: bankAsset,
        animationSource: animationBank,
        ownerTags,
        parent: options.parent,
        dynamicBounds: options.dynamicBounds,
      },
    )
    const status = next.getVisualStatus?.()
    if (
      status?.animated !== true ||
      status.animationTargetsResolved !== true
    ) {
      next.destroy()
      throw new Error('Synty animation bank could not bind to the visual rig')
    }
    return next
  }

  const cachedVisual = getCachedAsset(requestedVariant)
  const cachedBank = animationBank ? getCachedAsset(animationBank) : undefined
  let active: CharacterHandle
  if (cachedVisual && cachedBank) {
    try {
      active = spawnPrimary(cachedVisual, cachedBank)
      activeVariant = requestedVariant
    } catch (reason) {
      error = reason instanceof Error ? reason.message : String(reason)
      active = spawnCharacter(
        app,
        fallbackVariant,
        position,
        seed,
        {
          ownerTags,
          parent: options.parent,
          dynamicBounds: options.dynamicBounds,
        },
      )
    }
  } else {
    active = spawnCharacter(
      app,
      fallbackVariant,
      position,
      seed,
      {
        ownerTags,
        parent: options.parent,
        dynamicBounds: options.dynamicBounds,
      },
    )
  }

  const hydrate = async (): Promise<void> => {
    let replacement: CharacterHandle | undefined
    try {
      if (!animationBank) throw new Error('Synty animation bank is not catalogued')
      const [visualAsset, bankAsset] = await Promise.all([
        ensureAssetLoaded(app, requestedVariant),
        ensureAssetLoaded(app, animationBank),
      ])
      if (destroyed || activeVariant === requestedVariant) return
      replacement = spawnPrimary(visualAsset, bankAsset)
      if (station) replacement.setStation(station)
      replacement.setAnimState(currentState)
      replacement.setActive?.(sceneActive)
      active.destroy()
      active = replacement
      replacement = undefined
      activeVariant = requestedVariant
      error = undefined
    } catch (reason) {
      replacement?.destroy()
      if (destroyed) return
      error = reason instanceof Error ? reason.message : String(reason)
      console.warn(
        `[characters] ${requestedVariant} + ${animationBank ?? '<missing bank>'} ` +
        `unavailable; keeping ${fallbackVariant}: ${error}`,
      )
    }
  }
  if (activeVariant !== requestedVariant) void hydrate()

  return {
    get entity(): pc.Entity { return active.entity },

    getVisualStatus(): CharacterVisualStatus {
      const activeStatus = active.getVisualStatus?.()
      return {
        requested: requestedVariant,
        active: activeVariant,
        fallback: activeVariant !== requestedVariant,
        animated: activeVariant === requestedVariant
          ? activeStatus?.animated === true
          : activeStatus?.animated,
        animationPlaying: activeStatus?.animationPlaying,
        animationClipCount: activeStatus?.animationClipCount,
        ...(typeof activeStatus?.animationAssetId === 'number'
          ? { animationAssetId: activeStatus.animationAssetId }
          : {}),
        ...(activeStatus?.animationTargetsResolved !== undefined
          ? { animationTargetsResolved: activeStatus.animationTargetsResolved }
          : {}),
        ...(activeStatus?.animationSource
          ? { animationSource: activeStatus.animationSource }
          : {}),
        ...(error ? { error } : {}),
        sceneActive: activeStatus?.sceneActive ?? sceneActive,
      }
    },

    setAnimState(state: VoxelAvatarState): void {
      if (currentState === 'slumped' && state !== 'slumped') {
        console.warn(
          `[characters] suppressed Synty wrapper slumped -> ${state} for ` +
          `${seed.slice(0, 12)} (status flap upstream)`,
        )
        return
      }
      currentState = state
      active.setAnimState(state)
    },

    setActive(nextActive: boolean): void {
      sceneActive = nextActive
      active.setActive?.(nextActive)
    },

    setStation(next: StationPlacement): void {
      station = next
      active.setStation(next)
    },

    destroy(): void {
      destroyed = true
      active.destroy()
    },
  }
}

/**
 * Keep a known-good office avatar visible while the on-demand Synty proof is
 * loading. The licensed rig replaces it only after the container and every
 * named clip pass validation. A failed fetch or incompatible GLB never leaves
 * a bind-pose mannequin or an empty station.
 */
export function spawnSyntyCharacterProof(
  app: pc.Application,
  position: [number, number, number],
  seed: string,
  fallbackVariant: HumanVariant = pickVariant(seed),
): CharacterHandle {
  let currentState: VoxelAvatarState = 'idle'
  let sceneActive = true
  let station: StationPlacement | null = null
  let destroyed = false
  let error: string | undefined
  let activeVariant: CharacterVariant = fallbackVariant

  const spawnPrimary = (): CharacterHandle => spawnCharacter(
    app,
    SYNTY_PROOF_VARIANT,
    position,
    seed,
  )
  const cachedPrimary = getCachedAsset(SYNTY_PROOF_VARIANT)
  let active = cachedPrimary && assetHasRequiredClips(cachedPrimary)
    ? spawnPrimary()
    : spawnCharacter(app, fallbackVariant, position, seed)
  if (active.entity.name.includes(SYNTY_PROOF_VARIANT)) activeVariant = SYNTY_PROOF_VARIANT

  const hydrate = async (): Promise<void> => {
    try {
      const asset = await ensureAssetLoaded(app, SYNTY_PROOF_VARIANT)
      if (destroyed) return
      if (!assetHasRequiredClips(asset)) {
        throw new Error('required Synty animation clips are missing')
      }
      if (activeVariant === SYNTY_PROOF_VARIANT) return
      const next = spawnPrimary()
      if (station) next.setStation(station)
      next.setAnimState(currentState)
      next.setActive?.(sceneActive)
      active.destroy()
      active = next
      activeVariant = SYNTY_PROOF_VARIANT
    } catch (reason) {
      error = reason instanceof Error ? reason.message : String(reason)
      console.warn(`[characters] ${SYNTY_PROOF_VARIANT} unavailable; keeping ${fallbackVariant}: ${error}`)
    }
  }
  if (activeVariant !== SYNTY_PROOF_VARIANT) void hydrate()

  return {
    get entity(): pc.Entity { return active.entity },

    getVisualStatus(): CharacterVisualStatus {
      return {
        requested: SYNTY_PROOF_VARIANT,
        active: activeVariant,
        fallback: activeVariant !== SYNTY_PROOF_VARIANT,
        sceneActive,
        ...(error ? { error } : {}),
      }
    },

    setAnimState(state: VoxelAvatarState): void {
      currentState = state
      active.setAnimState(state)
    },

    setActive(nextActive: boolean): void {
      sceneActive = nextActive
      active.setActive?.(nextActive)
    },

    setStation(next: StationPlacement): void {
      station = next
      active.setStation(next)
    },

    destroy(): void {
      destroyed = true
      active.destroy()
    },
  }
}
